import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';

import { useLazyGetMyEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { useLazyGetOrganizationQuery } from '@/redux/slices/organizationApiSlice';
import { useAppDispatch } from '@/redux/store';
import { UserOrganization } from '@/types/users';
import { getDisplayFullName } from 'common/utils/getFullName';

type SetUserOrganizationProps = {
  selectedOrgId?: string;
  selectedOrgRoles?: UserOrganization['roles'];
  selectedEmployeeId?: UserOrganization['employeeId'];
  userDataRequestCompleted: boolean;
};

export const useRequestUserOrganization = ({
  selectedOrgId,
  selectedOrgRoles,
  selectedEmployeeId,
  userDataRequestCompleted,
}: SetUserOrganizationProps) => {
  const [isRequestingOrgData, setIsRequestingOrgData] = useState(false);
  const [orgDataRequestCompleted, setOrgDataRequestCompleted] = useState(false);

  const dispatch = useAppDispatch();

  const [getOrganization, { isError: organizationError }] =
    useLazyGetOrganizationQuery();

  const [getMyEmployee, { isError: employeeError }] =
    useLazyGetMyEmployeeQuery();

  useEffect(() => {
    const setUserOrganization = async () => {
      if (
        userDataRequestCompleted &&
        !orgDataRequestCompleted &&
        !isRequestingOrgData &&
        selectedOrgId &&
        selectedOrgRoles
      ) {
        // set loading state to true
        setIsRequestingOrgData(true);

        await getOrganization(selectedOrgId, true);

        // update the flags to unblock the page loading
        setOrgDataRequestCompleted(true);
        setIsRequestingOrgData(false);

        // request employee data (won't block the page loading and its response will be automatically saved by an extraReducer)
        // Use the employee directory as it doesn't require the caller to have any specific permissions.
        if (selectedEmployeeId) {
          getMyEmployee(selectedOrgId, true)
            .unwrap()
            .then(response => {
              // Now that we have the employee name, set the Datadog RUM user's name field
              datadogRum.setUserProperty('name', getDisplayFullName(response));
            });
        }
      }
    };

    setUserOrganization();
  }, [
    orgDataRequestCompleted,
    isRequestingOrgData,
    selectedOrgId,
    selectedOrgRoles,
    selectedEmployeeId,
    getOrganization,
    getMyEmployee,
    dispatch,
    userDataRequestCompleted,
  ]);

  return {
    organizationError,
    employeeError,
    isRequestingOrgData,
    orgDataRequestCompleted,
  };
};
